<template>
    <div class="section-wrapper">
        <!-- Reason Dropdown -->
        <text-blocks-generic
        v-if="getTransfersReasons.length === 1"
        :params="onlyOneTransferReasonParams"/>
        <dropdown-generic
        v-else
        id="internationalTransferReasonLeafId"
        class="dropdown-container"
        :params="transferReasonsParams"
        :options="parsedTransferReasonsList"
        :selectedOption="formData.internationalTransferReasonLeafId"
        @clicked="reasonChangeHandler"/>
        <!-- Operation Type Dropdown -->
        <dropdown-generic
        v-if="!!getOperationTypesList.length && formData.internationalTransferReasonLeafId"
        id="internationalTransferOperationTypeId"
        class="dropdown-container"
        :key="formData.internationalTransferOperationTypeId"
        :params="transferOperationTypeParams"
        :options="parsedTransferOperationTypes"
        :selectedOption="formData.internationalTransferOperationTypeId"
        @clicked="operationTypeChangeHandler"/>
        <!-- Operation Category Dropdown -->
        <dropdown-generic
        v-if="!!getCategoriesList.length && formData.internationalTransferOperationTypeId && getCategoriesList.length > 1"
        id="internationalTransferPaymentCategoryId"
        class="dropdown-container"
        :params="operationCategoryParams"
        :options="parsedOperationCategory"
        :selectedOption="formData.internationalTransferPaymentCategoryId"
        @clicked="operationCategoryChangeHandler"/>
        <!-- Operation Category Text Block -->
        <text-blocks-generic
        v-if="!!getCategoriesList.length && formData.internationalTransferOperationTypeId && getCategoriesList.length === 1 && getCategoriesList.find(obj => obj.id === formData.internationalTransferPaymentCategoryId)"
        :params="operationCategoryBlockParams"/>
        <!-- Expenses Dropdown -->
        <dropdown-generic
        id="internationalTransferExpenseId"
        class="dropdown-container"
        :key="formData.internationalTransferExpenseId"
        :params="transferExpensesParams"
        :options="parsedTransferExpense"
        :selectedOption="formData.internationalTransferExpenseId"
        @clicked="[
            emit('updateFormData', { field: 'internationalTransferExpenseId', inputValue: $event }),
            clearFieldError('internationalTransferExpenseId')
        ]"/>
        <!-- Radio -->
        <selector-radio-group
        mandatory
        :label="t('transfers.internationalCorporate.genericLabels.transferType')"
        :radioParams="radioParams"
        @clicked="[
            emit('updateFormData', { field: 'transferType', inputValue: $event }),
            selectedInternationalCorporateDocuments(),
            docsClearError()
        ]"/>
        <div class="documents-wrapper" :key="`documentsFor${formData.transferType}`">
            <!-- File Uploader -->
            <file-uploader
            v-for="doc in parsedInternationalCorporateDocuments"
            :key="doc.id"
            id="prepaid"
            mandatory
            hideSizeNote
            :errorState="doc.errorState"
            :errorMessage="doc.errorMessage"
            :label="doc.label"
            :autoUpload="false"
            :uploaderIdentifier="doc.id"
            :removeFile="currentRemovingFile ? doc.id === currentRemovingFile : removeFile"
            :initialFile="formData.transferTypeDocuments.find(file => file.id === doc.id)?.file"
            :showSuccessFeedback="hasInitialFiles.find(file => file.id === doc.id)?.hasInitialFile"
            @removeShowSuccessFeedback="removeFileHandler(doc.id)"
            @file-removed="removeFileHandler(doc.id)"
            @upload-success="uploadedFileHandler($event, doc.id)">
                <input-generic
                v-if="doc.id === 'MERCHANDISE_INVOICE'"
                id="invoiceNumber"
                :params="invoiceNumberParams"
                :inputValue="formData.invoiceNumber"
                @input="[emit('updateFormData', { field: 'invoiceNumber', inputValue: $event})]"/>
                <input-calendar
                v-if="doc.id === 'MERCHANDISE_INVOICE'"
                id="emissionDateInvoice"
                :params="emissionDateInvoiceParams"
                :disabledDates="{ from: new Date() }"
                :inputValue="formData.invoiceDate"
                @input="[emit('updateFormData', { field: 'invoiceDate', inputValue: $event })]"/>
            </file-uploader>
        </div>
    </div>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import TextBlocksGeneric from '@/components/generic/textBlocks/Generic'
import SelectorRadioGroup from '@/components/generic/selectors/radio/RadioGroup'
import FileUploader from '@/components/generic/uploader/FileUploader'
import InputGeneric from '@/components/generic/inputs/Generic'
import InputCalendar from '@/components/generic/inputs/Calendar'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue && Assets */
import { ref, computed, watch, onMounted } from 'vue'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getTransfersReasons, getOperationTypesList, getCategoriesList, getTransfersExpenses, getInternationalCorporateTypes } = mapGetters(moduleMapper.TRANSFERS_INTERNATIONAL_CORPORATE)

/** Props
@formData {Object} - Object with form data
@showInputsErrors {Boolean} - Prop to trigger display of inputs errors
@clearForm {Boolean} - Boolean controller to clear form and errors
@fieldsWithError {Array} - Input List with error MDW provides to use
*/
const props = defineProps({
    prepaidUploadData: Object,
    formData: Object,
    showInputsErrors: Boolean,
    clearForm: Boolean,
    fieldsWithError: Array
})

const emit = defineEmits([
    'updateFormData',
    'requestOperationsTypes',
    'requestCategories',
    'resetShowInputsErrors',
    'formIsCleared',
    'scrollTo',
    'updateTransferTypeDocument',
    'clearAllDocuments'
])

const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

/*************************************************
*                                                *
*               Transfer Reasons                 *
*                                                *
*************************************************/
const transferReasonsParams = ref({
    labelText: t('transfers.internationalCorporate.genericLabels.transferReasons'),
    placeholder: t('transfers.internationalCorporate.genericLabels.transferReasonsPlaceholder'),
	mandatory: true,
    errorState: false,
    errorMessage: ''
})

const parsedTransferReasonsList = computed(() => {
    return getTransfersReasons.value.map(reason => ({
        ...reason,
        id: reason.id,
        text: reason.description
    }))
})

const reasonChangeHandler = event => {
    if (event === props.formData.internationalTransferReasonLeafId) return

    emit('updateFormData', { field: 'internationalTransferReasonLeafId', inputValue: event })

    clearFormErrors()

    emit('requestOperationsTypes')
}

/*************************************************
*                                                *
*                Operation Types                 *
*                                                *
*************************************************/
const onlyOneTransferReasonParams = computed(() => {
    return {
            label: t('transfers.internationalCorporate.genericLabels.operationPurpose'),
            content: getTransfersReasons.value.find(reason => reason.id === props.formData.internationalTransferReasonLeafId).description
        }
})

const transferOperationTypeParams = ref({
    labelText: t('transfers.internationalCorporate.genericLabels.operationType'),
    placeholder: t('transfers.internationalCorporate.step1.placeholders.operationType'),
	mandatory: true,
    errorState: false,
    errorMessage: ''
})

const parsedTransferOperationTypes = computed(() => {
    return getOperationTypesList.value.map(type => ({
        ...type,
        id: type.id,
        text: type.description
    }))
})

const operationTypeChangeHandler = event => {
    if (event === props.formData.internationalTransferOperationTypeId) return

    emit('updateFormData', { field: 'internationalTransferOperationTypeId', inputValue: event })

    clearFormErrors()

    emit('requestCategories')
}

/*************************************************
*                                                *
*              Operation Categories              *
*                                                *
*************************************************/
const operationCategoryParams = ref({
    labelText: t('transfers.internationalCorporate.genericLabels.operationCategory'),
    placeholder: t('transfers.internationalCorporate.step1.placeholders.operationCategory'),
	mandatory: true,
    errorState: false,
    errorMessage: ''
})

const parsedOperationCategory = computed(() => {
    return getCategoriesList.value.map(type => ({
        ...type,
        id: type.id,
        text: type.description
    }))
})

const operationCategoryBlockParams = computed(() => {
    return {
            label: t('transfers.internationalCorporate.genericLabels.operationCategory'),
            content: getCategoriesList.value.find(category => category.id === props.formData.internationalTransferPaymentCategoryId).description
        }
})

const operationCategoryChangeHandler = event => {
    if (event === props.formData.internationalTransferPaymentCategoryId) return

    emit('updateFormData', { field: 'internationalTransferPaymentCategoryId', inputValue: event })

    clearFormErrors()
}

/*************************************************
*                                                *
*                Transfer Expense                *
*                                                *
*************************************************/
const transferExpensesParams = ref({
    labelText: t('transfers.internationalCorporate.genericLabels.transferExpense'),
    placeholder: t('transfers.internationalCorporate.step1.placeholders.transferExpense'),
	mandatory: true,
    errorState: false,
    errorMessage: ''
})

const parsedTransferExpense = computed(() => {
    return getTransfersExpenses.value.map(type => ({
        ...type,
        id: type.id,
        text: type.description
    }))
})

/*************************************************
*                                                *
*                     Uploads                    *
*                                                *
*************************************************/
const radioParams = computed(() => {
    return getInternationalCorporateTypes.value.map(obj => {
        return {
            id: obj.paymentType,
            text: obj.paymentTypeDescription,
            value: true,
            selected: props.formData.transferType === obj.paymentType,
            tooltipInfo: {
                description: obj.additionalDescription
            }
        }
    })
})

const parsedInternationalCorporateDocuments = ref([])

const selectedInternationalCorporateDocuments = dontClearDocuments => {
    const documents = getInternationalCorporateTypes.value.find(types => types.paymentType === props.formData.transferType)?.documentTypes

    parsedInternationalCorporateDocuments.value = documents.map(doc => {
        return {
            id: doc.internationalTransferDocumentType,
            label: doc.description,
            errorMessage: null,
            errorState: false
        }
    })

    if (!dontClearDocuments) {
        emit('clearAllDocuments')
        emit('updateFormData', { field: 'invoiceNumber', inputValue: '' })
        emit('updateFormData', { field: 'invoiceDate', inputValue: '' })
    }
}

/*************************************************
*                                                *
*         File Upload/Remove Handling            *
*                                                *
*************************************************/
const removeFile = ref(false)
const currentRemovingFile = ref(null)

const hasInitialFiles = ref(props.formData.transferTypeDocuments.map(obj => {
    return {
        id: obj.id,
        hasInitialFile: !!obj.file
    }
}))

const removeFileHandler = (id) => {
    removeFile.value = false
    currentRemovingFile.value = null

    hasInitialFiles.value = hasInitialFiles.value.map(obj => {
        return { ...obj, hasInitialFile: obj.id === id ? false : obj.hasInitialFile }
    })

    emit('updateTransferTypeDocument', { id: id })
}

const readFile = (file, id) => {
    if (!file.type.includes('pdf')) {
        currentRemovingFile.value = id
        feedbackToastData.value = errorObjectConstructor({
            message: t('fileUploader.invalidFileError'),
            dismissToastBtnText: t('commonButtons.gotIt')
        })
        showFeedbackToast.value = true
        removeFile.value = true
        return
    }

    const rawFile = file

	if (file) { // we only run if there is actually a file
		var reader = new FileReader() // initiate a new file reader
		reader.onload = async function (e) { // callback for the reader
			var base64Img = e.target.result // gets the image in base 64
			var binaryImg = convertDataURIToBinary(base64Img) // converts base 64 to binary
			var blob = new Blob([binaryImg], { type: file.type }) // creates the blob with the binary
			var fileName = file.name // get the name of tile
			var readyFile = { blob: blob, name: fileName, id: id, rawFile: rawFile, fileType: file.type?.split('/')[1] }
			fileReadComplete(readyFile)
		}
		reader.readAsDataURL(file) // starts the reading
	}
}

const fileReadComplete = fileInfo => {
    /* finding fileType in our fileName */
    const temp = fileInfo.name.split('.')
    /* blob -> fileInfo.blob */
    emit('updateTransferTypeDocument', {
        id: fileInfo.id,
        file: fileInfo.rawFile,
        fileBlob: fileInfo.blob,
        fileName: temp[0],
        fileType: fileInfo.fileType
    })
}

/**
* aux function to convert a DATAURI to BINARY
*/
const convertDataURIToBinary = dataURI => {
	var BASE64_MARKER = ';base64,'
	var base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
	var base64 = dataURI.substring(base64Index)
	var raw = window.atob(base64)
	var rawLength = raw.length
	var array = new Uint8Array(new ArrayBuffer(rawLength))
	for (var i = 0; i < rawLength; i++) {
		array[i] = raw.charCodeAt(i)
	}
	return array
}

const uploadedFileHandler = (event, id) => {
    hasInitialFiles.value = hasInitialFiles.value.map(obj => {
        return { ...obj, hasInitialFile: obj.id === id ? false : obj.hasInitialFile }
    })

    readFile(event, id)
    docsClearError(id)
}

const invoiceNumberParams = ref({
	labelText: t('transfers.internationalCorporate.genericLabels.invoiceNumber'),
    placeholder: t('transfers.internationalCorporate.step1.placeholders.invoiceNumber'),
    mandatory: true,
	errorState: false,
    errorMessage: null
})

const emissionDateInvoiceParams = ref({
	labelText: t('transfers.internationalCorporate.genericLabels.invoiceDate'),
    placeholder: t('transfers.internationalCorporate.step1.placeholders.invoiceDate'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/
const operationsMandatoryList = ref([
    'internationalTransferReasonLeafId',
    'internationalTransferOperationTypeId',
    'internationalTransferPaymentCategoryId',
    'internationalTransferExpenseId'
])

const fieldsWithErrorMessage = ref({
    internationalTransferReasonLeafId: transferReasonsParams.value,
    internationalTransferOperationTypeId: transferOperationTypeParams.value,
    internationalTransferPaymentCategoryId: operationCategoryParams.value,
    internationalTransferExpenseId: transferExpensesParams.value
})

const showInputsErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, operationsMandatoryList.value)

    if (mandatoryFieldsEmptyList.length) {
        mandatoryFieldsEmptyList.forEach(fieldName => {
            fieldsWithErrorMessage.value[fieldName].errorMessage = t('mandatoryFieldError')
            fieldsWithErrorMessage.value[fieldName].errorState = true
        })

        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))
    }

    if (props.fieldsWithError.length) {
        props.fieldsWithError.forEach(field => {
            if (fieldsWithErrorMessage.value[field.parsedFieldOnError]) {
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorState = true
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorMessage = field.errorMessage
            }
        })

        emit('scrollTo', getElementOffsetTopPosition(props.fieldsWithError[0]))
    }

    parsedInternationalCorporateDocuments.value = parsedInternationalCorporateDocuments.value.map(doc => {
        return {
            ...doc,
            errorState: !props.formData.transferTypeDocuments.find(obj => obj.id === doc.id)?.file
        }
    })
}

watch(() => props.showInputsErrors, newVal => {
	if (newVal) showInputsErrorsHandler()
	emit('resetShowInputsErrors')
})

/**********************************************************************
*                                                                     *
*                           Clear Field Errors                        *
*                                                                     *
***********************************************************************/
const clearFieldError = (fieldName) => {
    fieldsWithErrorMessage.value[fieldName].errorMessage = null
    fieldsWithErrorMessage.value[fieldName].errorState = false
}

const docsClearError = (docId) => {
    parsedInternationalCorporateDocuments.value = parsedInternationalCorporateDocuments.value.map(doc => {
        return {
            ...doc,
            errorState: !docId ? false : (docId === doc.id ? false : doc.errorState)
        }
    })
}

const clearFormErrors = () => {
	for (var fieldName in fieldsWithErrorMessage.value) {
		clearFieldError(fieldName)
	}
}

watch(() => props.clearForm, () => {
    if (!props.clearForm) return

    hasInitialFiles.value = []
	clearFormErrors()
    docsClearError()
    removeFile.value = true
	emit('formIsCleared')
})

onMounted(() => {
    selectedInternationalCorporateDocuments('dontClearDocuments')
})
</script>

<style lang="scss" scoped>
.section-wrapper {
    width: 100%;
    gap: 24px;

    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;

    .input-elements-container{
        margin-top: 24px;
    }

    .documents-wrapper {
        width: 100%;

        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-direction: column;
        gap: 24px;
    }
}

:deep(.tooltip-tiny) {
    min-width: 268px;
}
</style>
